'use es6';

import { useState } from 'react';
import PropTypes from 'prop-types';
import { Set as ImmutableSet } from 'immutable';
import EmailAssetDialogContainer from 'reporting-action-components/reportingActions/emailReport/dialog/EmailAssetDialogContainer';
import ExportTypeOptionsSelect from './ExportTypeOptionsSelect';
import IncludeReportsSelection from './IncludeReportsSelection';
import FormattingOptions from './FormattingOptions';
import { stringify } from 'hub-http/helpers/params';
import { getId, getName } from 'reporting-action-components/data/schemas/dashboardSchema';
import { DEFAULT_EXPORT_TYPE, IMAGE_EXPORT_TYPES } from 'reporting-action-components/reportingActions/shared/emailLogic';
import { DASHBOARD_OBJECT_TYPE } from 'dashboard-lib/public/emails/emails';
import { useUserRoleForDashboard } from 'reporting-action-components/utils/trackingUtils';
import { RouteKeys, getRoutePath, createDashboardUrl } from '../../../constants/route/DashboardRoute';
import { ApplySavedDashboardFiltersEmailOptions } from './ApplySavedDashboardFiltersEmailOptions';
import { FILTER_SET_QUERY_PARAM } from '../../../lib/dashboardFilters/dashboard-filter-query-params-util';
import { isUnsupportedDataExportType } from 'reporting-action-components/reportingActions/shared/emailLogic';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const REPORTS_PER_PAGE_PPT = 4;
export const REPORTS_PER_PAGE_PDF = 2;
const EmailDashboardDialogContainer = ({
  appUsageTracker,
  onEditSuccess = () => {},
  dashboard,
  scheduledSharedAsset = {}
}) => {
  const {
    exportType: prevExportType,
    reportsPerPage: prevReportsPerPage,
    filterId: initialFilterSetId
  } = scheduledSharedAsset;
  const [exportType, setExportType] = useState(prevExportType || DEFAULT_EXPORT_TYPE.NONE);
  const [reportsPerPage, setReportsPerPage] = useState(prevReportsPerPage || 1);
  const [showReportsSelection, setShowReportsSelection] = useState(false);
  const [selectedReports, setSelectedReports] = useState(ImmutableSet());
  const [filterSetId, setFilterSetId] = useState(initialFilterSetId);
  const validationError = showReportsSelection && selectedReports.isEmpty();
  const shouldRenderFormattingOptions = exportType === IMAGE_EXPORT_TYPES.PDF || exportType === IMAGE_EXPORT_TYPES.PPTX;
  const getReportsPerPage = multiplePerPage => multiplePerPage ? exportType === IMAGE_EXPORT_TYPES.PDF ? REPORTS_PER_PAGE_PDF : REPORTS_PER_PAGE_PPT : 1;
  const dashboardId = getId(dashboard);
  const previewUrl = createDashboardUrl({
    template: getRoutePath(RouteKeys.EXPORT),
    pathParam: {
      dashboardId
    },
    queryParam: {
      isPreview: true,
      selectedReports: selectedReports.toJS(),
      reportsPerPage: getReportsPerPage(reportsPerPage > 1),
      exportType: exportType === IMAGE_EXPORT_TYPES.PDF ? 'pdf' : null
    }
  });
  const userRole = useUserRoleForDashboard(dashboard);
  const renderEmailFilterOptions = () => {
    return /*#__PURE__*/_jsx(ApplySavedDashboardFiltersEmailOptions, {
      dashboard: dashboard,
      filterSetId: initialFilterSetId,
      onApplyDashboardFilters: nextFilterSetId => {
        setFilterSetId(nextFilterSetId);
      },
      isUnsupportedDataExportType: isUnsupportedDataExportType(exportType)
    });
  };
  return /*#__PURE__*/_jsx(EmailAssetDialogContainer, {
    appUsageTracker: appUsageTracker,
    assetSpecificTrackingProps: {
      role: userRole
    },
    dashboard: dashboard,
    onEditSuccess: onEditSuccess,
    scheduledSharedAsset: scheduledSharedAsset,
    assetSpecificPayload: {
      exportType,
      reportsPerPage: getReportsPerPage(reportsPerPage > 1),
      filterId: filterSetId,
      queryString: `?${stringify({
        selectedReports: selectedReports.toJS()
      })}`
    },
    validationError: validationError,
    objectId: dashboardId,
    objectType: DASHBOARD_OBJECT_TYPE,
    assetName: getName(dashboard),
    previewUrl: filterSetId ? previewUrl.concat(`&${FILTER_SET_QUERY_PARAM}=${filterSetId}`) : previewUrl,
    resetSelectedReports: () => {
      setSelectedReports(ImmutableSet());
      setShowReportsSelection(false);
    },
    renderAdditionalFields: shouldRenderEmailSchedulingComponents => /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ExportTypeOptionsSelect, {
        setExportType: setExportType,
        exportType: exportType,
        dashboard: dashboard
      }), !shouldRenderEmailSchedulingComponents &&
      /*#__PURE__*/
      // Because dashboards are subject to change, we only render the reports selection component if the customer is sending the email now.
      _jsx(IncludeReportsSelection, {
        exportType: exportType,
        selectedReports: selectedReports,
        setSelectedReports: setSelectedReports,
        setShowReportsSelection: setShowReportsSelection,
        showReportsSelection: showReportsSelection,
        dashboard: dashboard
      }), shouldRenderFormattingOptions && /*#__PURE__*/_jsx(FormattingOptions, {
        setReportsPerPage: setReportsPerPage,
        reportsPerPage: reportsPerPage
      }), renderEmailFilterOptions()]
    })
  });
};
EmailDashboardDialogContainer.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  dashboard: PropTypes.object.isRequired,
  onEditSuccess: PropTypes.func,
  scheduledSharedAsset: PropTypes.object
};
export default EmailDashboardDialogContainer;